import { FC, useEffect, useState } from 'react';

import { useI18nValues } from '@/entities/vfo/hooks/useI18nValues';
import { AccessLevel, ActivationStatus, EntityType, UserStatus } from '@/entities/vfo/types';
import { Avatar, PermissionGuard } from '@/features/vfo/components';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useChat } from '@/features/vfo/hooks/useChat';
import { useIsFounder } from '@/features/vfo/hooks/useIsFounder';
import { useIsMemberInPermission } from '@/features/vfo/hooks/useIsMemberInPermissions';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { usePermissions } from '@/features/vfo/hooks/usePermissions';
import { COLORS } from '@/shared/constants/colors';
import { getCountryName } from '@/shared/dictionaries/countries';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { useAppLogout } from '@/shared/hooks/useAppLogout';
import { useAuthStore } from '@/shared/store/auth.store';
import { useProfileStore } from '@/shared/store/profile.store';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { Button } from '@/shared/uikitV2/Button';
import { dates } from '@/shared/utils/dates';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { BtnClose } from '@/widgets/modals/components/BtnClose';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { VfoProfileModalProps } from '@/widgets/modals/types';

import { ButtonDestructiveText } from '../ButtonDestructiveText';

import { AccessLevelTooltip } from './AccessLevelTooltip';
import {
  Actions,
  Body,
  ButtonLogout,
  CaseItem,
  CaseList,
  CaseName,
  Container,
  Email,
  FounderWrapper,
  Header,
  HeaderActions,
  InfoBlock,
  InfoColumn,
  InvitationPending,
  Label,
  ProfileHeader,
  Section,
  Spaced,
  StatusCellStyled,
  StyledDrawer,
  Tag,
  UserInfo,
  UserName,
  Value,
} from './styled';

export const VfoProfileModal: FC<VfoProfileModalProps> = ({ initialMember, onOpenCase }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'vfoProfile' });
  const [member, setMember] = useState(initialMember);
  const [isLoading, setIsLoading] = useState(false);
  const values = useI18nValues();

  const { selectChat, findByCaseId } = useChat();
  const { account } = useAuthStore();
  const { setFullName } = useProfileStore();
  const isFounder = useIsFounder();
  const modals = useModalsStore();
  const { getMemberCases } = useCases();
  const { fetchMembers, currentUser } = useMembers();
  const { fetchChats } = useChat();
  const { userRoles, hasAnyRole } = usePermissions();
  const { appLogout } = useAppLogout();

  const isInPermissions = useIsMemberInPermission(member);

  const router = useAppRouter();

  const isUserSelf = account?.id === member.profileId;
  const showAccessLevel =
    !member.isFounder &&
    member?.activationStatus === ActivationStatus.ACTIVE &&
    (isFounder || currentUser?.profileId === member.profileId);

  useEffect(() => {
    if (currentUser && isUserSelf && !isLoading) {
      setMember(currentUser);
      setFullName(currentUser.firstName ?? '', currentUser.lastName ?? '');
    }
  }, [isUserSelf, currentUser, isLoading]);

  const handleClose = () => {
    modals.closeModal();
  };

  const refetchData = async () => {
    setIsLoading(true);
    await fetchMembers();
    await fetchChats();
    setIsLoading(false);
  };

  const handleEditClick = () => {
    modals.openModal({
      type: 'SetupProfileModal',
      withIntroduction: false,
      initialData: member,
      isMember: !isFounder,
      onSave: refetchData,
    });
  };

  const memberFullName =
    member.firstName && member.lastName && member.activationStatus === ActivationStatus.ACTIVE
      ? `${member.firstName} ${member.lastName}`
      : t('invitedMember');

  const memberCases = member.profileId ? getMemberCases(member.profileId) : [];

  const displayBasicInfo =
    (member.createdAt && !member.isFounder) || member.lastActiveDate || member.country;

  const showMessageButton =
    member.activationStatus === ActivationStatus.ACTIVE &&
    (isFounder || member.isFounder) &&
    member.profileId !== account?.id;

  const handleMessageClick = () => {
    handleClose();
    const privateCase = memberCases.find((item) => item.isPrivateCase);
    if (privateCase) {
      const chat = findByCaseId(privateCase.id);
      if (chat) selectChat(chat.id);
    }
    modals.openModal({
      type: 'VfoChatModal',
    });
  };

  return (
    <StyledDrawer width={500} open={true} closable={false} footer={null} onClose={handleClose}>
      <Container>
        <Header>
          <Title size={14} bold>
            {t('title')}
          </Title>
          <HeaderActions>
            {isUserSelf && (
              <Button
                type="text"
                icon={<Icon name="pencil-simple" size="s20" />}
                onClick={handleEditClick}
              >
                {t('edit')}
              </Button>
            )}
            <BtnClose onClick={handleClose} />
          </HeaderActions>
        </Header>
        <Body className="uikitScrollbar">
          <ProfileHeader>
            <Avatar size="s64" member={member} />
            <UserInfo>
              <Tag>
                {member.isFounder ? (
                  <FounderWrapper>
                    <Icon name={'medal'} size="s12" />
                    <span>{t('founder')}</span>
                  </FounderWrapper>
                ) : member.tag ? (
                  values.tag(member.tag)
                ) : null}
              </Tag>
              <UserName>{memberFullName}</UserName>
              {(isFounder || isUserSelf) && <Email>{member.email}</Email>}
            </UserInfo>
            <StatusCellStyled
              status={isUserSelf ? UserStatus.ONLINE : member.status}
              activationStatus={member.activationStatus}
            />
          </ProfileHeader>

          {showMessageButton && (
            <Button type="default" onClick={handleMessageClick}>
              {t('message')}
            </Button>
          )}

          {member.activationStatus === ActivationStatus.INVITED &&
            member.profileId !== account?.id && (
              <InvitationPending>{t('invitationPending')}</InvitationPending>
            )}

          {displayBasicInfo && (
            <Section>
              <b>{t('basicInfo')}</b>
              {member.activationStatus === ActivationStatus.ACTIVE ? (
                <InfoColumn>
                  {member.createdAt && !member.isFounder && (
                    <InfoBlock>
                      <Label>{t('startDate')}</Label>
                      <Value>{dates.formatLongDate2(member.createdAt)}</Value>
                    </InfoBlock>
                  )}
                  {member.lastActiveDate && (
                    <InfoBlock>
                      <Label>{t('lastActive')}</Label>
                      <Value>{dates.formatLongDate2(member.lastActiveDate)}</Value>
                    </InfoBlock>
                  )}
                  {member.country && (
                    <InfoBlock>
                      <Label>{t('country')}</Label>
                      <Value>{getCountryName(member.country)}</Value>
                    </InfoBlock>
                  )}
                </InfoColumn>
              ) : (
                <Label>{t('none')}</Label>
              )}
            </Section>
          )}

          {showAccessLevel && (
            <Section>
              <Spaced>
                <b>{t('access')}</b>
                {isFounder && (
                  <Button
                    size="middle"
                    type="text"
                    icon={<Icon name="pencil-simple" size="s20" />}
                    style={{
                      height: 'auto',
                    }}
                    onClick={() => {
                      modals.closeModal();
                      modals.openModal({
                        type: 'AccessLevelModal',
                        member,
                        openProfile: true,
                      });
                    }}
                  >
                    {t('editAccess')}
                  </Button>
                )}
              </Spaced>
              <InfoColumn>
                <InfoBlock>
                  <Label>{t('accessLevel')}</Label>
                  <Value>
                    <span>{values.accessLevel(member?.accessLevel ?? userRoles[0].roleCode)}</span>
                    <AccessLevelTooltip
                      accessLevel={member?.accessLevel ?? userRoles[0].roleCode}
                    />
                  </Value>
                </InfoBlock>
              </InfoColumn>
            </Section>
          )}

          {!(isFounder && member.isFounder) && (
            <Section>
              <Spaced>
                <div>
                  <b>{t('cases')} </b>
                  {memberCases.length > 0 && <span>({memberCases.length})</span>}
                </div>

                {!isFounder && isInPermissions && currentUser?.profileId !== member.profileId && (
                  <PermissionGuard
                    role={[AccessLevel.CONNECTED, AccessLevel.DELEGATED, AccessLevel.EXECUTIVE]}
                  >
                    <Button
                      size="middle"
                      type="text"
                      icon={<Icon name="pencil-simple" size="s20" />}
                      style={{
                        height: 'auto',
                      }}
                      onClick={() => {
                        modals.closeModal();
                        modals.openModal({
                          type: 'AddOrEditCaseModal',
                          preselectedMember: initialMember.profileId,
                        });
                      }}
                    >
                      {t('createCaseChat')}
                    </Button>
                  </PermissionGuard>
                )}
              </Spaced>
              <CaseList>
                {!memberCases.length && <Label>{t('none')}</Label>}
                {memberCases.map((c) => {
                  const hasConnectedLevel =
                    hasAnyRole([AccessLevel.CONNECTED]) && c.profileId !== currentUser?.profileId;

                  const canDelete =
                    !c.isPrivateCase && !hasConnectedLevel && (isFounder || !member.isFounder);

                  return (
                    <CaseItem key={c.id}>
                      <CaseName
                        onClick={() => {
                          router.push(`/vfo/case/${c.id}`);
                          onOpenCase?.();
                          handleClose();
                        }}
                      >
                        {c.title}
                      </CaseName>
                      <PermissionGuard
                        role={[AccessLevel.CONNECTED, AccessLevel.DELEGATED, AccessLevel.EXECUTIVE]}
                      >
                        {canDelete && (
                          <Button
                            icon={<Icon name="x-circle" size="s16" />}
                            onClick={() => {
                              if (!member.profileId) return;
                              modals.openModal({
                                type: 'RemoveFromCaseModal',
                                caseId: c.id,
                                entityType: EntityType.Member,
                                entityId: member.profileId,
                              });
                            }}
                            style={{
                              padding: 0,
                              height: 'fit-content',
                              width: 'fit-content',
                              color: COLORS.systemDark300,
                              background: 'none',
                            }}
                          />
                        )}
                      </PermissionGuard>
                    </CaseItem>
                  );
                })}
              </CaseList>
              {member.activationStatus === ActivationStatus.ACTIVE && isFounder && (
                <Button
                  type="default"
                  onClick={() => {
                    if (!member?.profileId) return;
                    modals.openModal({
                      type: 'AddToCaseModal',
                      entityType: EntityType.Member,
                      entityId: member.profileId,
                    });
                  }}
                >
                  {t('addToCase')}
                </Button>
              )}
              {member.activationStatus === ActivationStatus.INVITED && isFounder && (
                <Button
                  style={{
                    width: 'fit-content',
                    fontSize: 14,
                  }}
                  type="text"
                  icon={<Icon name="arrow-counter-clockwise" size="s16" />}
                  onClick={() => {
                    if (!member.invitationId) return;
                    modals.openModal({
                      type: 'ResendInvitationModal',
                      id: member.invitationId,
                    });
                  }}
                >
                  {t('resendInvitation')}
                </Button>
              )}
            </Section>
          )}
        </Body>
        {member.activationStatus === ActivationStatus.ACTIVE && isFounder && !member.isFounder && (
          <Actions>
            <ButtonDestructiveText
              icon={<Icon name="trash" size="s20" />}
              onClick={() => {
                if (!member.profileId) return;
                modals.openModal({
                  type: 'RemoveMemberModal',
                  profileId: member.profileId,
                  onFinish: modals.closeModal,
                });
              }}
            >
              {t('removeMember')}
            </ButtonDestructiveText>
          </Actions>
        )}
        {isUserSelf && !isFounder && (
          <Actions>
            <ButtonLogout
              type="text"
              size="middle"
              icon={<Icon name="sign-out" size="s20" />}
              onClick={() => {
                modals.openModal({
                  type: 'ConfirmModalV2',
                  variant: 'confirm',
                  title: t('logout'),
                  bodyText: t('logoutConfirmation'),
                  confirmText: t('logout'),
                  onConfirm: appLogout,
                });
              }}
            >
              {t('logout')}
            </ButtonLogout>
          </Actions>
        )}
      </Container>
    </StyledDrawer>
  );
};
